import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  styled,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  Security,
  AdminPanelSettings,
} from "@mui/icons-material";
import PermissionDialog, { Module } from "./PermissionDialog";
import apiClient from "../api/client";
import { getAuthMode, getAuthToken } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useModulePermission } from "../utils/useModulePermission";

interface SettingsPageProps {
  startDate: string | null;
  endDate: string | null;
}

interface Role {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  is_deleted: boolean;
  created_at?: string;
  updated_at?: string;
}

interface FetchRolesResponse {
  items: Role[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface UserAssignedRole {
  id: number;
  userName: string;
  email: string;
  assignedRole: string;
  status: string;
}


interface UserRole {
  id: number;
  name: string;
  description: string | null;
  permissions: any[];
}

interface User {
  id: number;
  oid: string;
  email: string;
  name: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  role: UserRole | null;
  created_at: string;
}

interface FetchUsersResponse {
  items: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}


// Styled Toggle Switch component with text inside
const StatusToggle = styled(Switch)(({ theme }) => ({
  width: 80,
  height: 32,
  padding: 0,

  "& .MuiSwitch-switchBase": {
    padding: 2,
    transitionDuration: "300ms",

    "&.Mui-checked": {
      transform: "translateX(46px)",
      color: "#fff",

      "& + .MuiSwitch-track": {
        backgroundColor: "#059669",
        opacity: 1,
        border: 0,

        "&:before": {
          content: '"Active"',
          position: "absolute",
          left: 10,
        },
        "&:after": {
          content: '""',
        },
      },
    },

    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.5,
    }
  },

  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 28,
    height: 28,
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },

  "& .MuiSwitch-track": {
    borderRadius: 34 / 2,
    backgroundColor: "#dc2626",
    opacity: 1,
    transition: "background-color 300ms",
    position: "relative",

    "&:before, &:after": {
      display: "flex",
      alignItems: "center",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: "10px",
      fontWeight: 600,
      color: "#fff",
    },

    "&:before": {
      content: '""',
      left: 10,
    },

    "&:after": {
      content: '"Inactive"',
      right: 8,
    }
  }
}));



const SettingsPage: React.FC<SettingsPageProps> = ({ startDate, endDate }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { can } = useModulePermission("Settings");
  const canSettingsRead = can("read");
  const canSettingsUpdate = can("update");
  const canSettingsDelete = can("delete");

  // Signed-in user (used to prevent self-delete)
  const { user: signedInUser } = useAuth();
  const signedInUserId: number | null =
    typeof signedInUser?.id === "number" ? signedInUser.id : null;
  const signedInUserEmail: string =
    typeof signedInUser?.email === "string" ? signedInUser.email : "";
  const signedInUserOid: string =
    typeof signedInUser?.oid === "string" ? signedInUser.oid : "";

  const isSignedInUser = (u: User) => {
    if (signedInUserId !== null && u.id === signedInUserId) return true;
    if (signedInUserEmail && u.email === signedInUserEmail) return true;
    if (signedInUserOid && u.oid === signedInUserOid) return true;
    return false;
  };

  // Loading states
  const [rolesLoading, setRolesLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);


  // Roles state and pagination
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesPage, setRolesPage] = useState(0);
  const [rolesPageSize, setRolesPageSize] = useState(10);
  const [rolesTotal, setRolesTotal] = useState(0);
  const [rolesRefreshCounter, setRolesRefreshCounter] = useState(0);


  // Users state and pagination
  const [users, setUsers] = useState<User[]>([]);
  const [usersPage, setUsersPage] = useState(0);
  const [usersPageSize, setUsersPageSize] = useState(20);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersRefreshCounter, setUsersRefreshCounter] = useState(0);



  // User Assigned Roles state
  const [userAssignedRoles, setUserAssignedRoles] = useState<
    UserAssignedRole[]
  >([]);

  useEffect(() => {
    fetchModules();
  }, []);

  // Fetch roles with pagination (new API: items, total, page, page_size, total_pages)
  useEffect(() => {
    let cancelled = false;
    setRolesLoading(true);
    const page = rolesPage + 1; // API is 1-based
    apiClient
      .get<FetchRolesResponse>('/api/v1/rolems/get_all_roles', {
        params: { page, page_size: rolesPageSize },
      })
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        setRoles(Array.isArray(data?.items) ? data.items : []);
        setRolesTotal(typeof data?.total === 'number' ? data.total : 0);
      })
      .catch(() => {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: 'Failed to fetch roles',
            severity: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rolesPage, rolesPageSize, rolesRefreshCounter]);

  // Fetch users with pagination (new API: items, total, page, page_size, total_pages)
  useEffect(() => {
    let cancelled = false;
    setUsersLoading(true);
    const page = usersPage + 1; // API is 1-based
    apiClient
      .get<FetchUsersResponse>('/api/v1/auth/users', {
        params: { page, page_size: usersPageSize },
      })
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        setUsers(Array.isArray(data.items) ? data.items : []);
        setUsersTotal(typeof data.total === 'number' ? data.total : 0);
      })
      .catch(() => {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: 'Failed to fetch users',
            severity: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [usersPage, usersPageSize, usersRefreshCounter]);



  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRoleId) return;

    try {
      await apiClient.patch(`/api/v1/auth/users/${selectedUser.id}/role`, {
        role_id: selectedRoleId
      });
      setSnackbar({
        open: true,
        message: "Role assigned successfully!",
        severity: "success",
      });
      refetchUsers();
      closeUserDialog();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Failed to assign role",
        severity: "error",
      });
    }
  };


  const refetchRoles = () => setRolesRefreshCounter((c) => c + 1);


  // Fetch modules for permissions
  const fetchModules = async () => {
    try {
      const response = await apiClient.get('/api/v1/rolems/get_all_modules');
      const modulesData = response.data || [];
      setModules(modulesData.map((m: any) => ({
        slug: m.name.toLowerCase().replace(/\s+/g, '_'),
        label: m.name,
        module_id: m.id,
        permissions: { read: false, write: false, update: false, delete: false, upload: false },
      })));
    } catch (error) {
    }
  };


  const refetchUsers = () => setUsersRefreshCounter((c) => c + 1);



  // Dialog states
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleDialogMode, setRoleDialogMode] = useState<"create" | "edit" | "view">("create");
  const [userDialogMode, setUserDialogMode] = useState<"assign" | "view">("assign");

  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState<Partial<Role>>({ name: "", description: "", });

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<"role" | "userAssignedRole" | "user">("role");
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  // Permissions dialog state
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionRole, setPermissionRole] = useState<Role | null>(null);
  const [moduleData, setModuleData] = useState<Module[]>([]);

  // Modules state for permissions
  const [modules, setModules] = useState<Module[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };


  const getStatusChip = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <Chip label="Approved" size="small" sx={{ backgroundColor: '#dcfce7', color: '#166534' }} />;
      case 'PENDING':
        return <Chip label="Pending" size="small" sx={{ backgroundColor: '#fef3c7', color: '#92400e' }} />;
      case 'REJECTED':
        return <Chip label="Rejected" size="small" sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };




  const handleStatusChange = async (userId: number, newStatus: string) => {
    if (newStatus === "APPROVED") {
      await handleApproveUser(userId);
    }

    if (newStatus === "REJECTED") {
      await handleRejectUser(userId);
    }
  }


  const handleApproveUser = async (userId: number) => {
    try {
      await apiClient.post(`/api/v1/auth/users/${userId}/approve`);
      setSnackbar({
        open: true,
        message: "User approved successfully!",
        severity: "success",
      });
      refetchUsers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Failed to approve user",
        severity: "error",
      });
    }
  };


  const handleRejectUser = async (userId: number) => {
    try {
      await apiClient.post(`/api/v1/auth/users/${userId}/reject`);
      setSnackbar({
        open: true,
        message: "User rejected",
        severity: "success",
      });
      refetchUsers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Failed to reject user",
        severity: "error",
      });
    }
  };


  // User handlers
  const openUserDialog = (mode: "assign" | "view", user: User) => {
    if (mode === "view" && !canSettingsRead) return;
    if (mode === "assign" && !canSettingsUpdate) return;
    setUserDialogMode(mode);
    setSelectedUser(user);
    setSelectedRoleId(user.role?.id || null);
    setUserDialogOpen(true);
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
    setSelectedUser(null);
    setSelectedRoleId(null);
  };

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);





  const openRoleDialog = (mode: "create" | "edit" | "view", role?: Role) => {
    if (mode === "view" && !canSettingsRead) return;
    if (mode === "edit" && !canSettingsUpdate) return;
    setRoleDialogMode(mode);
    if (role) {
      setSelectedRole(role);
      setRoleForm({
        name: role.name,
        description: role.description,
      });
    } else {
      setSelectedRole(null);
      setRoleForm({ name: "", description: "" });
    }
    setRoleDialogOpen(true);
  };


  // Open permissions dialog
  const openPermissionsDialog = async (role: Role) => {
    if (!canSettingsUpdate) return;
    setPermissionRole(role);
    try {
      // Fetch current permissions for the role
      const response = await apiClient.get(`/api/v1/rolems/get_role_permissions/${role.id}`);
      const rolePermissions = response.data || [];
      // Map modules with current permissions
      const updatedModules = modules.map(mod => {
        const rolePerm = rolePermissions.find((rp: any) => rp.module_id === mod.module_id);
        return {
          ...mod,
          permissions: rolePerm?.permissions || { read: false, write: false, update: false, delete: false, upload: false }
        };
      });
      setModuleData(updatedModules);

    } catch (error) {
      setModuleData(modules);
    }
    setPermissionDialogOpen(true);
  };

  const closeRoleDialog = () => {
    setRoleDialogOpen(false);
    setSelectedRole(null);
    setRoleForm({ name: "", description: "" });
  };

  const handleRoleSave = async () => {
    try {
      const roleData = {
        name: roleForm.name || "",
        description: roleForm.description || "",
      };

      if (roleDialogMode === "create") {
        await apiClient.post(`/api/v1/rolems/create_role`, roleData);

        setSnackbar({
          open: true,
          message: "Role created successfully!",
          severity: "success",
        });

      } else if (roleDialogMode === "edit" && selectedRole) {
        await apiClient.put(`/api/v1/rolems/edit_role/${selectedRole.id}`, roleData);

        setSnackbar({
          open: true,
          message: "Role updated successfully!",
          severity: "success",
        });
      }

      await refetchRoles();
      closeRoleDialog();

    } catch (error) {
      const err: any = error;
      const errMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        (typeof err === "string" ? err : JSON.stringify(err)) ||
        "Failed to save role";

      setSnackbar({
        open: true,
        message: errMsg,
        severity: "error",
      });
    }
  };


  const confirmDeleteRole = (id: number) => {
    if (!canSettingsDelete) return;
    setDeleteType("role");
    setDeleteId(id);
    setDeleteUser(null);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = (user: User) => {
    if (!canSettingsDelete) return;
    if (isSignedInUser(user)) {
      setSnackbar({
        open: true,
        message: "You cannot delete your own account.",
        severity: "error",
      });
      return;
    }
    setDeleteType("user");
    setDeleteId(user.id);
    setDeleteUser(user);
    setDeleteDialogOpen(true);
  };



  // Toggle Role Status handler
  const handleRoleStatusToggle = async (id: number, currentStatus: boolean) => {
    if (!canSettingsUpdate) return;
    try {
      await apiClient.patch(`/api/v1/rolems/patch_role/${id}`, {
        is_active: !currentStatus
      });
      refetchRoles();
      setSnackbar({
        open: true,
        message: `Role status updated`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to update role status",
        severity: "error",
      });
    }
  };


  // Delete handler
  const handleDelete = async () => {
    if (!canSettingsDelete) return;
    if (deleteId === null) return;
    setDeleteLoading(true);
    try {
      if (deleteType === "role") {
        await apiClient.patch(`/api/v1/rolems/patch_role/${deleteId}`, { is_deleted: true });
        setSnackbar({
          open: true,
          message: "Role deleted successfully!",
          severity: "success",
        });
        await refetchRoles();
      } else if (deleteType === "userAssignedRole") {
        setUserAssignedRoles(userAssignedRoles.filter((u) => u.id !== deleteId));
        setSnackbar({
          open: true,
          message: "User role assignment deleted successfully!",
          severity: "success",
        });
      } else if (deleteType === "user") {
        // Extra safety: never allow self-delete
        if (signedInUserId !== null && deleteId === signedInUserId) {
          setSnackbar({
            open: true,
            message: "You cannot delete your own account.",
            severity: "error",
          });
          return;
        }
        const authMode = getAuthMode();
        const token = getAuthToken();
        if (authMode === "bearer" && !token) {
          setSnackbar({
            open: true,
            message: "Missing auth token. Please login again.",
            severity: "error",
          });
          return;
        }

        await apiClient.delete(
          `/api/v1/auth/users/${deleteId}`,
          authMode === "bearer" && token
            ? { headers: { Authorization: `Bearer ${token}` } }
            : undefined
        );

        setSnackbar({
          open: true,
          message: "User deleted successfully!",
          severity: "success",
        });

        await refetchUsers();
      }

    } catch (error) {
      setSnackbar({
        open: true,
        message: (error as any)?.response?.data?.detail || "Failed to delete",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }

    setDeleteDialogOpen(false);
    setDeleteId(null);
    setDeleteUser(null);
  };


  const closePermissionsDialog = () => {
    setPermissionDialogOpen(false);
    setPermissionRole(null);
  };

  const togglePermission = (
    slug: string,
    action: "read" | "write" | "update" | "delete" | "upload"
  ) => {
    setModuleData((prevData) =>
      prevData.map((module) =>
        module.slug === slug
          ? {
            ...module,
            permissions: {
              ...module.permissions,
              [action]: !module.permissions[action],
            },
          }
          : module
      )
    );
  };



  const handlePermissionsSave = async () => {
    if (!permissionRole) return;

    try {
      // Build permissions payload
      const modulesPayload = moduleData
        .filter(mod => Object.values(mod.permissions).some(v => v))
        .map(mod => ({
          module_id: mod.module_id,
          permissions: Object.entries(mod.permissions)
            .filter(([_, enabled]) => enabled)
            .map(([action]) => action)
        }));

      await apiClient.post(`/api/v1/rolems/role_permission_create/${permissionRole.id}`, {
        modules: modulesPayload
      });

      setSnackbar({
        open: true,
        message: "Permissions saved successfully!",
        severity: "success",
      });
      closePermissionsDialog();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Failed to save permissions",
        severity: "error",
      });
    }
  };



  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Settings sx={{ fontSize: 32, color: "#1e40af" }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Settings
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: "#64748b" }}>
          Manage your application settings and preferences
        </Typography>
      </Box> */}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="Roles Management" />
          <Tab label="User Management" />
        </Tabs>
      </Box>

      {/* Tab 1 Content - Roles */}
      {activeTab === 0 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "#0f172a", fontWeight: 600 }}>
              Roles List
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openRoleDialog("create")}
              sx={{
                backgroundColor: "#1e40af",
                "&:hover": { backgroundColor: "#1e3a8a" },
              }}
            >
              Add Role
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Si.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rolesLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                      <Typography sx={{ mt: 2, color: "#64748b" }}>
                        Loading roles...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : roles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: "#64748b" }}>
                        No roles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  roles.map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{rolesPage * rolesPageSize + index + 1}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>
                        <StatusToggle
                          checked={row.is_active}
                          onChange={() => handleRoleStatusToggle(row.id, row.is_active)}
                          inputProps={{ "aria-label": "status toggle" }}
                          disabled={!canSettingsUpdate || row.name === 'Director' || row.name === 'Guest'}
                        />
                      </TableCell>


                      <TableCell align="center">
                            {/**
                             * For protected roles (Director / Guest) only allow View action.
                             * Disable Assign Permissions, Edit and Delete for those roles.
                             */}
                            {(() => {
                              const isProtectedRole = row.name === 'Director' || row.name === 'Guest';
                              return (
                                <>
                                  <Tooltip
                                    title={
                                      !canSettingsUpdate
                                        ? "You do not have update permission"
                                        : isProtectedRole
                                          ? "Assign Permissions disabled for this role"
                                          : "Assign Permissions"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => openPermissionsDialog(row)}
                                        sx={{ color: "#1e40af", opacity: !canSettingsUpdate || isProtectedRole ? 0.4 : 1 }}
                                        disabled={!canSettingsUpdate || isProtectedRole}
                                      >
                                        <Security fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title={canSettingsRead ? "View" : "You do not have read permission"}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => openRoleDialog("view", row)}
                                        sx={{ color: "#1e40af", opacity: canSettingsRead ? 1 : 0.4 }}
                                        disabled={!canSettingsRead}
                                      >
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip
                                    title={
                                      !canSettingsUpdate
                                        ? "You do not have update permission"
                                        : isProtectedRole
                                          ? "Edit disabled for this role"
                                          : "Edit"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => openRoleDialog("edit", row)}
                                        sx={{ color: "#d97706", opacity: !canSettingsUpdate || isProtectedRole ? 0.4 : 1 }}
                                        disabled={!canSettingsUpdate || isProtectedRole}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip
                                    title={
                                      !canSettingsDelete
                                        ? "You do not have delete permission"
                                        : isProtectedRole
                                          ? "Delete disabled for this role"
                                          : "Delete"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => confirmDeleteRole(row.id)}
                                        sx={{ color: "#dc2626", opacity: !canSettingsDelete || isProtectedRole ? 0.4 : 1 }}
                                        disabled={!canSettingsDelete || isProtectedRole}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              );
                            })()}
                          </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rolesTotal}
              page={rolesPage}
              onPageChange={(_, newPage) => setRolesPage(newPage)}
              rowsPerPage={rolesPageSize}
              onRowsPerPageChange={(e) => {
                const size = Number(e.target.value);
                setRolesPageSize(size);
                setRolesPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              showFirstButton
              showLastButton
            />
          </TableContainer>
        </Box>
      )}

      {/* Tab 2 Content - User Managment*/}
      {activeTab === 1 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "#0f172a", fontWeight: 600 }}>
              User Management
            </Typography>
            {/* <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openUserAssignedRoleDialog("create")}
              sx={{
                backgroundColor: "#1e40af",
                "&:hover": { backgroundColor: "#1e3a8a" },
              }}
            >
              Assign Role
            </Button> */}
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Si.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Assigned Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                      <Typography sx={{ mt: 2, color: "#64748b" }}>
                        Loading users...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: "#64748b" }}>
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    (() => {
                      const isSelf = isSignedInUser(user);
                      return (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        backgroundColor: user.status === 'PENDING' ? 'rgba(251, 191, 36, 0.1)' : 'inherit'
                      }}
                    >
                      <TableCell>{usersPage * usersPageSize + index + 1}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role?.name || 'No Role'}
                          size="small"
                          sx={{
                            backgroundColor: user.role?.name === 'Director' ? '#dbeafe' :
                              user.role?.name === 'Guest' ? '#f1f5f9' : '#e0e7ff',
                            color: user.role?.name === 'Director' ? '#1e40af' :
                              user.role?.name === 'Guest' ? '#64748b' : '#4338ca',
                          }}
                        />
                      </TableCell>
                      <TableCell>{getStatusChip(user.status)}</TableCell>
                      {/* <TableCell>
                        <StatusToggle
                          checked={user.status}
                          onChange={() => handleRoleStatusToggle(user.id, user.status === 'Active')}
                          inputProps={{ "aria-label": "status toggle" }}
                          disabled={row.name === 'Director' || row.name === 'Guest'}
                        />
                      </TableCell> */}

                      <TableCell align="center">
                   

                          <Tooltip title={canSettingsUpdate ? "Assign Role" : "You do not have update permission"}>
                                <span>
                                  <IconButton
                                    size="medium"
                                    onClick={() => openUserDialog('assign', user)}
                                    sx={{ color: "#d97706", opacity: canSettingsUpdate ? 1 : 0.4 }}
                                    disabled={!canSettingsUpdate}
                                  >
                                    <AdminPanelSettings fontSize="medium" />
                                  </IconButton>
                                </span>
                          </Tooltip>


                        <Tooltip title={canSettingsRead ? "View" : "You do not have read permission"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openUserDialog("view", user)}
                              sx={{ color: "#15478dff", opacity: canSettingsRead ? 1 : 0.4 }}
                              disabled={!canSettingsRead}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                      <Tooltip
                        title={
                          !canSettingsDelete
                            ? "You do not have delete permission"
                            : isSelf
                              ? "You can't delete your own account"
                              : "Delete"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            sx={{ color: "#dc2626", opacity: isSelf || !canSettingsDelete ? 0.4 : 1 }}
                            onClick={() => confirmDeleteUser(user)}
                            disabled={isSelf || !canSettingsDelete}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>



                      </TableCell>

                      {/* <TableCell>
                        <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openRoleDialog("edit", user)}
                              sx={{ color: "#d97706" }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                      </TableCell> */}

                    </TableRow>
                      );
                    })()
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={usersTotal}
              page={usersPage}
              onPageChange={(_, newPage) => setUsersPage(newPage)}
              rowsPerPage={usersPageSize}
              onRowsPerPageChange={(e) => {
                const size = Number(e.target.value);
                setUsersPageSize(size);
                setUsersPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              showFirstButton
              showLastButton
            />
          </TableContainer>
        </Box>
      )}

      {/* Role Dialog (Create/Edit/View) */}
      <Dialog
        open={roleDialogOpen}
        onClose={closeRoleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {roleDialogMode === "create"
              ? "Add New Role"
              : roleDialogMode === "edit"
                ? "Edit Role"
                : "View Role"}
          </Typography>
          <IconButton onClick={closeRoleDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={roleForm.name}
              onChange={(e) =>
                setRoleForm({ ...roleForm, name: e.target.value })
              }
              InputProps={{ readOnly: roleDialogMode === "view" }}

            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={roleForm.description}
              onChange={(e) =>
                setRoleForm({ ...roleForm, description: e.target.value })
              }
              InputProps={{ readOnly: roleDialogMode === "view" }}

            />
            {/* <FormControl fullWidth disabled={roleDialogMode === "view"}>
              <InputLabel>Status</InputLabel>
              <Select
                value={roleForm.status}
                label="Status"
                onChange={(e) =>
                  setRoleForm({ ...roleForm, status: e.target.value })
                }
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl> */}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeRoleDialog} variant="outlined">
            {roleDialogMode === "view" ? "Close" : "Cancel"}
          </Button>
          {roleDialogMode !== "view" && (
            <Button
              onClick={handleRoleSave}
              variant="contained"
              sx={{ backgroundColor: "#1e40af" }}
            >
              {roleDialogMode === "create" ? "Create" : "Save Changes"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* User Assigned Role Dialog (Create/Edit/View) */}
      <Dialog
        open={userDialogOpen}
        onClose={closeUserDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {userDialogMode === "view"
              ? "User Details"
              : "User Role Assignment"}
          </Typography>

          <IconButton onClick={closeUserDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        {/* <p>{JSON.stringify(selectedUser)}</p> */}
        <DialogContent dividers>
          {selectedUser && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField label="Name" fullWidth value={selectedUser.name} InputProps={{ readOnly: true }} />
              <TextField label="Email" fullWidth value={selectedUser.email} InputProps={{ readOnly: true }} />
              {/* <TextField label="Status" fullWidth value={selectedUser.status } InputProps={{ readOnly: true }}  /> */}
              <FormControl fullWidth >
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedUser.status}
                  inputProps={{ readOnly: userDialogMode === "view" }}
                  label="Status"
                  onChange={(e) => handleStatusChange(selectedUser.id, e.target.value)}>
                  <MenuItem value="APPROVED" sx={{ textTransform: "capitalize" }}>Approved</MenuItem>
                  <MenuItem value="PENDING" sx={{ textTransform: "capitalize" }}>Pending</MenuItem>
                  <MenuItem value="REJECTED" sx={{ textTransform: "capitalize" }}>Rejected</MenuItem>
                </Select>
              </FormControl>


              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
              <Select
                  value={selectedRoleId ?? selectedUser.role?.id ?? ""}
                  inputProps={{ readOnly: userDialogMode === "view" }}
                  label="Role"
                  onChange={(e) => setSelectedRoleId(Number(e.target.value))}
                >
                  {roles
                    .filter((r) => r.is_active)
                    .map((role) => (
                      <MenuItem key={role.id} value={role.id} sx={{ textTransform: "capitalize" }}>
                        {role.name}
                      </MenuItem>
                    ))}
                </Select>

              </FormControl>

            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeUserDialog} variant="outlined">
            {userDialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {userDialogMode !== 'view' && (
            <Button
              onClick={handleAssignRole}
              variant="contained"
              sx={{ backgroundColor: "#1e40af" }}
              disabled={!selectedRoleId}
            >
              Assign Role
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteDialogOpen(false);
          setDeleteId(null);
          setDeleteUser(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {deleteType === "user" ? (
              <>
                Are you sure you want to delete this user
                {deleteUser?.email ? ` (${deleteUser.email})` : ""}? This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to delete this {deleteType}? This action cannot be undone.
              </>
            )}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              if (deleteLoading) return;
              setDeleteDialogOpen(false);
              setDeleteId(null);
              setDeleteUser(null);
            }}
            variant="outlined"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleteLoading || !canSettingsDelete}>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Permissions Dialog */}
      <PermissionDialog
        open={permissionDialogOpen}
        onClose={closePermissionsDialog}
        moduleData={moduleData}
        togglePermission={togglePermission}
        onSubmit={handlePermissionsSave}
        roleName={permissionRole?.name}
      />
    </Box>
  );
};

export default SettingsPage;
